import { styled } from "@mui/material/styles";
import { AcquireBookDataGrid } from "@/components/DataGrid";
import {
  type GridColDef,
  GridToolbarContainer,
  type GridValueFormatterParams,
  type GridValueGetterParams,
} from "@mui/x-data-grid-pro";
import { Button as MuiButton, Stack, Tooltip } from "@mui/material";
import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { type AnalyzedRealEstate } from "@/features/allMatters/types";
import OutputMenu from "@/features/allMatters/components/OutputMenu";
import { AllMattersActionSection } from "@/features/allMatters/components/AllMattersActionSection";
import { useReadOnlyUser } from "@/hooks/useReadOnlyUser";
import { useShowSendToSamboKun } from "@/features/partner/hooks/useShowSendToSamboKun";
import { useSamboKunLimit } from "@/features/partner/hooks/useSamboKunLimit";
import {
  getSamboKunSendDisableReason,
  samboKunSendDisabledTooltip,
} from "@/features/partner/utils/isSendDisabled";
import { SendToSamboKunDialog } from "@/features/partner/components/SendToSamboKunDialog";
import { createSamboKunColumn } from "@/features/partner/components/samboKunColumn";

export interface IProps {
  row: AnalyzedRealEstate[] | undefined;
  requestId: number | undefined;
  disabledAction: boolean;
  isPropertyDetailOptionCredit?: boolean;
  // 不動産登記取得（PictureRequest）に紐づく解析かどうか。
  // false の場合は参謀くん連携 API がエラーになるため、連携ボタンを無効化する。
  hasTriggerPictureRequest?: boolean;
  /** 解析リクエストに紐づく物件数（連携ボタンの上限超過判定に使用）。 */
  succeededCount?: number;
  onDownloadComplete?: () => void;
  onOpenRow?: (row: AnalyzedRealEstate) => void;
  v2Toolbar?: React.ReactNode;
  v2Caption?: React.ReactNode;
  v2StatusColumn?: GridColDef;
}

const StyledDataGrid = styled(AcquireBookDataGrid)`
  // テーブルのセルをクリックしても青い枠が表示されないようにする
  & .MuiDataGrid-cell:focus-within,
  & .MuiDataGrid-cell:focus {
    outline: none !important;
  }

  // テーブルのカラムヘッダをクリックしても青い枠が表示されないようにする
  & .MuiDataGrid-columnHeader:focus-within,
  & .MuiDataGrid-columnHeader:focus {
    outline: none !important;
  }

  // テーブルヘッダーの背景色を設定
  .MuiDataGrid-columnHeaders {
    line-height: 1.5em !important;
  }

  // テーブルタイトルを太字に
  .MuiDataGrid-columnHeaderTitle {
    font-weight: 700;
  }
`;

export const makeGridColDef = (showSendToSamboKun: boolean): GridColDef[] => {
  const baseColumns: GridColDef[] = [
    {
      field: "id",
      headerName: "No",
      width: 90,
      sortable: false,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "realEstateType",
      headerName: "不動産種別",
      width: 100,
      sortable: false,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "location",
      headerName: "所在",
      width: 300,
      sortable: false,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "chibanKaokuNumber",
      headerName: "地番または家屋番号",
      width: 200,
      sortable: false,
      align: "left",
      headerAlign: "left",
      valueGetter: (
        params: GridValueGetterParams<unknown, AnalyzedRealEstate>
      ): string => {
        return params.row.realEstateType === "土地"
          ? params.row.chiban ?? ""
          : params.row.kaokuNumber ?? "";
      },
    },
    {
      field: "pictureUpdateTime",
      type: "dateTime",
      headerName: "PDF取得日時",
      width: 220,
      sortable: false,
      align: "center",
      headerAlign: "center",
      valueFormatter: (params: GridValueFormatterParams<string>) => {
        const value = params.value;
        if (value == null) return "";
        return format(new Date(value), "yyyy/MM/dd HH:mm");
      },
    },
    {
      field: "createdAt",
      type: "dateTime",
      headerName: "解析日時",
      width: 220,
      sortable: false,
      align: "center",
      headerAlign: "center",
      valueFormatter: (params: GridValueFormatterParams<string>) => {
        const value = params.value;
        if (value == null) return "";
        return format(new Date(value), "yyyy/MM/dd HH:mm");
      },
    },
  ];

  return showSendToSamboKun
    ? [...baseColumns, createSamboKunColumn()]
    : baseColumns;
};

// エクセル出力と参謀くん連携。v1 はツールバーに、v2 は表の上の v1 の領域に置く。
const OutputActions: React.FC<IProps> = (props) => {
  const isReadOnly = useReadOnlyUser();
  const disableOutput = props.disabledAction || props.requestId == null;
  const requestId = props.requestId ?? 0;
  const showSendToSamboKun = useShowSendToSamboKun();
  const { maxPropertiesPerRequest, isError: isLimitError } = useSamboKunLimit({
    enabled: showSendToSamboKun,
  });
  const [samboKunDialogOpen, setSamboKunDialogOpen] = useState<boolean>(false);

  // 連携ボタンの Disable 理由（undefined は "unknown-count" としてそのまま渡す）。
  // ローディング/エラー/物件数不明/上限超過を区別してツールチップ文言を切り替える。
  const disableReason = getSamboKunSendDisableReason(
    props.succeededCount,
    maxPropertiesPerRequest,
    isLimitError
  );
  const isLinkDisabled = disableReason !== "ok";
  // ボタン無効時のツールチップ文言。limit-loading は空文字にマップされるので、
  // Tooltip 側で空タイトル時のホバーを抑止する。
  const samboKunTooltipText =
    props.hasTriggerPictureRequest === false
      ? "不動産登記取得結果に紐づく解析のみ連携できます"
      : disableReason !== "ok"
      ? samboKunSendDisabledTooltip[disableReason]
      : "";

  // LaunchDarkly フラグや SSO 状態変化で showSendToSamboKun が false 化したとき、
  // ダイアログ表示状態を閉じておく。閉じておかないと、再度 true になった際に
  // ユーザー操作なしでダイアログが復活してしまう。
  useEffect(() => {
    if (!showSendToSamboKun) {
      setSamboKunDialogOpen(false);
    }
  }, [showSendToSamboKun]);

  return (
    <>
      <Stack
        direction={"row"}
        spacing={1}
        useFlexGap={Boolean(props.v2Toolbar)}
        flexWrap={props.v2Toolbar ? "wrap" : undefined}
        sx={props.v2Toolbar ? { minWidth: 0, maxWidth: "100%" } : undefined}
      >
        <OutputMenu
          disabled={disableOutput}
          requestId={requestId}
          isReadOnly={isReadOnly}
          isPropertyDetailOptionCredit={props.isPropertyDetailOptionCredit}
          onDownloadComplete={props.onDownloadComplete}
        />
        {showSendToSamboKun && (
          <Tooltip
            title={samboKunTooltipText}
            placement="top"
            arrow
            disableHoverListener={
              (props.hasTriggerPictureRequest !== false && !isLinkDisabled) ||
              samboKunTooltipText === ""
            }
          >
            <span>
              <MuiButton
                variant="outlined"
                disabled={
                  disableOutput ||
                  props.hasTriggerPictureRequest === false ||
                  isLinkDisabled
                }
                onClick={() => {
                  setSamboKunDialogOpen(true);
                }}
              >
                参謀くんへ連携
              </MuiButton>
            </span>
          </Tooltip>
        )}
      </Stack>
      {showSendToSamboKun && props.requestId != null && (
        <SendToSamboKunDialog
          open={samboKunDialogOpen}
          onClose={() => {
            setSamboKunDialogOpen(false);
          }}
          target="allMatters"
          analyzeRequestId={props.requestId}
        />
      )}
    </>
  );
};

// v2 はボタンを表の外に出し、ツールバーには表の説明だけを置く。
const CustomToolbar: React.FC<IProps> = (props) => (
  <GridToolbarContainer
    sx={{
      // https://mui.com/material-ui/react-button/#sizes と同様のpaddingを設定
      padding: "6px 16px",
    }}
  >
    {props.v2Toolbar ? (
      <Stack spacing={1} sx={{ minWidth: 0, width: "100%" }}>
        {props.v2Caption}
      </Stack>
    ) : (
      <OutputActions {...props} />
    )}
  </GridToolbarContainer>
);

export const FeedGrid: React.FC<IProps> = (props) => {
  // LaunchDarkly フラグ + ローカルストレージの SSO 連携情報（ssoPartnerIds に
  // 参謀くんパートナー ID が含まれるか）の両方を満たすときのみ参謀くん連携 UI を表示。
  const showSendToSamboKun = useShowSendToSamboKun();
  const colDef = React.useMemo(
    () => [
      ...makeGridColDef(showSendToSamboKun),
      ...(props.v2StatusColumn ? [props.v2StatusColumn] : []),
    ],
    [showSendToSamboKun, props.v2StatusColumn]
  );
  // v1 は成功行のみ。v2 は v1 の失敗行も開いて独立した解析結果を確認できる。
  const rows = React.useMemo(
    () =>
      (props.row ?? []).filter(
        (row) => props.onOpenRow != null || row.status === "succeeded"
      ),
    [props.row, props.onOpenRow]
  );

  const grid = (
    <StyledDataGrid
      components={{
        Toolbar: CustomToolbar,
      }}
      componentsProps={{
        toolbar: {
          requestId: props.requestId,
          disabledAction: props.disabledAction,
          isPropertyDetailOptionCredit: props.isPropertyDetailOptionCredit,
          hasTriggerPictureRequest: props.hasTriggerPictureRequest,
          succeededCount: props.succeededCount,
          onDownloadComplete: props.onDownloadComplete,
          v2Toolbar: props.v2Toolbar,
          v2Caption: props.v2Caption,
        },
      }}
      {...(props.onOpenRow
        ? {
            onRowClick: (params, event) => {
              if (
                !(event.target as HTMLElement).closest(
                  "button, a, input, [role=checkbox]"
                )
              ) {
                props.onOpenRow?.(params.row as AnalyzedRealEstate);
              }
            },
            onCellKeyDown: (params, event) => {
              if (
                event.key === "Enter" &&
                !(event.target as HTMLElement).closest(
                  "button, a, input, [role=checkbox]"
                )
              ) {
                props.onOpenRow?.(params.row as AnalyzedRealEstate);
              }
            },
            sx: { "& .MuiDataGrid-row": { cursor: "pointer" } },
          }
        : {})}
      stickyHeader={true}
      autoHeight
      disableColumnMenu
      disableSelectionOnClick={true}
      rows={rows}
      columns={colDef}
      sortModel={[
        {
          field: "requestedAt",
          sort: "desc",
        },
      ]}
    />
  );
  if (!props.v2Toolbar) return grid;

  // v1 と v2 の出力を別の領域に分け、表の説明は表のツールバーに付ける。
  return (
    <Stack sx={{ minWidth: 0 }}>
      <Stack spacing={2} sx={{ p: 2, minWidth: 0 }}>
        <AllMattersActionSection title="データ化結果の出力">
          <OutputActions {...props} />
        </AllMattersActionSection>
        {props.v2Toolbar}
      </Stack>
      {grid}
    </Stack>
  );
};
